import React, { useState, useEffect } from 'react'; // NEW: useEffect has been imported
import InteractiveFloorPlan from '../../components/InteractiveFloorPlan/InteractiveFloorPlan';
import Lightbox from '../../components/Lightbox/Lightbox'; // Added Lightbox import
import { floorPlans } from '../../data/floorPlanData.js';
import styles from './FloorPlansContent.module.css';

// Define the order and labels for the level selectors
const levels = [
  { key: 'second-floor', label: 'Second Floor' },
  { key: 'first-floor', label: 'First Floor' },
  { key: 'basement', label: 'Basement' },
];

const FloorPlansContent = () => {
  const [currentLevel, setCurrentLevel] = useState('basement');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false); // New state for Lightbox
  const [activeRoom, setActiveRoom] = useState({
    title: 'Facility Floor Plans',
    info: 'Select a floor level from the panel on the left, then click any highlighted room on the interactive map to view its details and see a photo.',
    roomImages: [],
  });

  // NEW: This useEffect hook watches for changes to `currentLevel`.
  // When it changes, it resets the activeRoom and gallery index to their default state.
  useEffect(() => {
    setActiveRoom({
      title: 'Facility Floor Plans',
      info: 'Select a floor level from the panel on the left, then click any highlighted room on the interactive map to view its details and see a photo.',
      roomImages: [],
    });
    setCurrentImageIndex(0);
  }, [currentLevel]);

  const levelData = floorPlans[currentLevel];

  // When a new area is selected, update the active room and reset the image index
  const handleAreaSelect = (area) => {
    setActiveRoom(area);
    setCurrentImageIndex(0);
  };

  // When clicking off the map, reset the view and the image index
  const handleResetView = () => {
    setActiveRoom({
      title: 'Facility Floor Plans',
      info: 'Select a floor level from the panel on the left, then click any highlighted room on the interactive map to view its details and see a photo.',
      roomImages: [],
    });
    setCurrentImageIndex(0);

  };

  // Handlers for gallery navigation
  const handleNextImage = () => {
    setCurrentImageIndex(prevIndex => (prevIndex + 1) % activeRoom.roomImages.length);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(prevIndex => (prevIndex - 1 + activeRoom.roomImages.length) % activeRoom.roomImages.length);
  };

  return (
    <section className={styles.mainContainer}>

      <div className={styles.tipBanner}>
        <p className={styles.tipText}><strong>Tip:</strong> Click on the highlighted areas on the map below to view photos and details.</p>
      </div>

      <div className={`${styles.unifiedMapContainer} ${styles.floorPlanLayoutContainer}`}>
        <div className={styles.levelSelector}>
          {levels.map((level) => (
            <button
              key={level.key}
              className={`${styles.levelButton} ${currentLevel === level.key ? styles.active : ''}`}
              onClick={() => setCurrentLevel(level.key)}
            >
              {level.label}
            </button>
          ))}
        </div>

        <div className={styles.mapContainer}>
          {levelData ? (
            <InteractiveFloorPlan
              key={currentLevel}
              imageName={levelData.imageName}
              imageAlt={levelData.imageAlt}
              mapName={levelData.mapName}
              areas={levelData.areas}
              onAreaSelect={handleAreaSelect}
              onBackgroundClick={handleResetView}
            />
          ) : (
            <p>Floor plan data could not be loaded.</p>
          )}
        </div>
      </div>

      <div className={styles.activeRoomInfo}>
        <div className={styles.infoText}>
          <h3>{activeRoom.title}</h3>
          <p>{activeRoom.info}</p>
        </div>
        {activeRoom.roomImages && activeRoom.roomImages.length > 0 && (
          <div className={styles.infoImage}>
            <img
              src={activeRoom.roomImages[currentImageIndex]}
              alt={`${activeRoom.title} view ${currentImageIndex + 1}`}
              onClick={() => setIsLightboxOpen(true)}
              style={{ cursor: 'pointer' }}
              title="Click to view larger image"
            />
            {activeRoom.roomImages.length > 1 && (
              <>
                <button onClick={handlePrevImage} className={`${styles.galleryButton} ${styles.prevButton}`}>&#8249;</button>
                <button onClick={handleNextImage} className={`${styles.galleryButton} ${styles.nextButton}`}>&#8250;</button>
              </>
            )}
          </div>
        )}
      </div>

      {isLightboxOpen && activeRoom.roomImages.length > 0 && (
        <Lightbox
          images={activeRoom.roomImages}
          initialIndex={currentImageIndex}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}
    </section>
  );
};

export default FloorPlansContent;
